import styled from "@emotion/styled";
import { useEffect, useState } from 'react';
import { useNavigationPath, i18n, mediaQuery, NavigationService, openCallMeModal, showMessage, useFinishPayment, useGeneralSoContext, useGetOfferById, useGetSo, useIframeDetection, useSetPaymentErrors } from 'src/common';
import { Button, Flex, LoaderSquare, StatusMessage } from 'src/components';
import { footer, header } from 'src/theme';
import { AppRoutes } from "./_router/app.routes";

const ButtonContainerStyle = styled.div({
  marginTop: 32
})

const FlexStyle = styled(Flex)({
  minHeight: `calc(100vh - ${footer.mobileHeight + header.mobileHeight + 80}px)`,

  [mediaQuery('mobile')]: {
    minHeight: `calc(100vh - ${footer.height + header.mobileHeight + 80}px)`,
  },

  [mediaQuery('laptopL')]: {
    minHeight: `calc(100vh - ${footer.height + header.height + 80}px)`,
  },
});
type UrlParams = Record<string, string>;

const keyMapping: Record<string, string> = {
  CustomerFirstname: 'wsPayCustomerFirstName',
  CustomerSurname: 'wsPayCustomerLastName',
  ShoppingCartID: 'wsPayShoppingCartId',
  DateTime: 'wsPayDateTime',
  Amount: 'wsPayAmount',
  STAN: 'wsPaySTAN',
  Partner: 'wsPayPartner',
  WsPayOrderId: 'wsPayOrderId',
  PaymentType: 'wsPayPaymentType',
  CreditCardNumber: 'wsPayCreditCardNumber',
  PaymentPlan: 'wsPayPaymentPlan',
  Success: 'wsPaySuccess',
  ApprovalCode: 'wsPayApprovalCode',
  Signature: 'wsPayConfirmationSignature',
};

const CheckoutFailurePage = () => {
  const { forwardPath, backwardPath } = useNavigationPath();
  const [params, setParams] = useState<UrlParams | null>(null);
  const { offerId, soId } = useGeneralSoContext();
  const { refetch } = useGetSo();
  const { refetch: refetchOffer } = useGetOfferById();
  const [setPaymentErrors] = useSetPaymentErrors();
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<string | null>(null);
  const [finishPaymentForOffer] = useFinishPayment();

  const { iframeInfo, isLoading: iframeLoading, sendToParent, isInIframe } = useIframeDetection();


  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const paramsObject: UrlParams = Array.from(queryParams.entries()).reduce(
      (acc, [key, value]) => {
        const newKey = keyMapping[key];
        if (newKey) acc[newKey] = value;
        return acc;
      },
      {} as UrlParams
    );
    setParams(paramsObject);
  }, []);

  useEffect(() => {
    if (!soId || !offerId) {
      NavigationService.navigate(AppRoutes.WEBSHOP.PROPERTY_INFO);
      return;
    }
    refetch();
    refetchOffer();
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    setErrors(queryParams.get('ErrorCodes'));
  }, []);

  useEffect(() => {
    if (errors && !isSubmitted && offerId) {
      setIsSubmitted(true);
      setPaymentErrors({
        paymentErrors: errors.split(',')
      });
    }
  }, [errors, offerId]);

  useEffect(() => {
    if (errors && isInIframe && !iframeLoading) {
      sendToParent({
        type: 'CHECKOUT_FAILURE',
        data: {
          errors: errors
        }
      });
    }
  }, [errors, isInIframe, iframeLoading, iframeInfo, offerId, soId, sendToParent]);


  useEffect(() => {
    if (!params || !offerId) return;

    const wsPayParams = {
      wsPayAmount: params.wsPayAmount,
      wsPayApprovalCode: params.wsPayApprovalCode,
      wsPayConfirmationSignature: params.wsPayConfirmationSignature,
      wsPayCreditCardNumber: params.wsPayCreditCardNumber,
      wsPayCustomerFirstName: params.wsPayCustomerFirstName,
      wsPayCustomerLastName: params.wsPayCustomerLastName,
      wsPayDateTime: params.wsPayDateTime,
      wsPayOrderId: params.wsPayOrderId,
      wsPayPartner: params.wsPayPartner,
      wsPayPaymentPlan: params.wsPayPaymentPlan,
      wsPayPaymentType: params.wsPayPaymentType,
      wsPaySTAN: params.wsPaySTAN ?? "10231",
      wsPayShoppingCartId: params.wsPayShoppingCartId,
      wsPaySuccess: params.wsPaySuccess,
    };

    finishPaymentForOffer(wsPayParams)
      .catch(() => {
        showMessage({
          type: 'error',
          message: i18n.t('common.messages.policyErrorMessage'),
          duration: 5000,
          fixed: true,
        });
      })
      .finally(() => {
        window.sessionStorage.clear();
      });
  }, [params, offerId]);

  const paymentPath = soId && offerId
    ? forwardPath.replace(':soId', soId).replace(':offerId', offerId)
    : backwardPath;

  const Buttons = () => (
    <ButtonContainerStyle>
      <Flex vertical gap={32} align='center'>
        <div>
          <Button btnType='primary' onClick={() => NavigationService.navigate(paymentPath)}>
            {i18n.t('pages.checkoutFailurePage.tryAgainButton')}
          </Button>
        </div>
        <div>
          <Button btnType='textBlack' onClick={() => openCallMeModal()}>
            {i18n.t('pages.checkoutFailurePage.requestCallButton')}
          </Button>
        </div>
      </Flex>
    </ButtonContainerStyle>
  );

  return (
    <>
      {iframeInfo?.isInIframe && <LoaderSquare />}
      {!iframeInfo?.isInIframe && (
        <FlexStyle justify='center' align='center'>
          <StatusMessage
            buttons={<Buttons />}
            title={i18n.t('pages.checkoutFailurePage.title')}
            subtitle={i18n.t('pages.checkoutFailurePage.subtitle')}
          />
        </FlexStyle>
      )}
    </>
  );
}

export default CheckoutFailurePage;
